import React, { useState } from 'react';
import { CreditCard, Expense, Income, Loan, PayoffPlan } from '../types';
import { projectCardBalance, activeCoveringPlan } from '../utils/finance/cardLedger';
import { minimumPaymentForCard } from '../utils/finance/minimums';
import { toDayKey, toLocalDay } from '../utils/finance/dates';
import { RowActions, RowActionCell } from './RowActions';
import { PayoffPlanBuilder } from './payoff/PayoffPlanBuilder';
import { BalanceReconcileDialog, ReconcileChoice } from './payoff/BalanceReconcileDialog';
import { planToPayload } from './payoff/planPayload';
import { PayoffTimeline } from './payoff/PayoffTimeline';
import {
  PageHeader, EmptyState, FormScreen, useFormScreenScroll,
  Button, Tag, TagWeight, Field, Input, TYPE, cn,
} from './ui';

/** A date input gives 'YYYY-MM-DD'. `new Date()` on that reads it as UTC
 *  midnight, which is the day before in every US timezone — so build it from
 *  the parts instead. */
const dayFromInput = (value: string): Date | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

type CardDraft = {
  name: string; balance: number; interestRate: number; minimumPayment: number;
  minPaymentPrincipalPercent: number; creditLimit: number; dueDay: number;
  /** 'YYYY-MM-DD', or '' for none. */
  payoffDeadline: string;
};

/** One field set for add and edit. */
const CardFields: React.FC<{
  value: CardDraft;
  onChange: (next: CardDraft) => void;
  idPrefix: string;
}> = ({ value, onChange, idPrefix }) => {
  const num = (v: string, fallback: number) => (v === '' ? fallback : parseFloat(v) || fallback);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
      <Field label="Card name" htmlFor={`${idPrefix}-name`}>
        <Input id={`${idPrefix}-name`} type="text" required value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })} />
      </Field>
      <Field label="Current balance" htmlFor={`${idPrefix}-balance`}>
        <Input id={`${idPrefix}-balance`} type="number" required step="0.01" value={value.balance}
          onChange={(e) => onChange({ ...value, balance: num(e.target.value, 0) })} />
      </Field>
      <Field label="Interest rate" hint="Annual percentage rate." htmlFor={`${idPrefix}-apr`}>
        <Input id={`${idPrefix}-apr`} type="number" required step="0.01" value={value.interestRate}
          onChange={(e) => onChange({ ...value, interestRate: num(e.target.value, 0) })} />
      </Field>
      <Field label="Credit limit" htmlFor={`${idPrefix}-limit`}>
        <Input id={`${idPrefix}-limit`} type="number" required step="0.01" value={value.creditLimit}
          onChange={(e) => onChange({ ...value, creditLimit: num(e.target.value, 0) })} />
      </Field>
      <Field
        label="Minimum floor"
        hint="The issuer's fixed floor, e.g. $35. The app computes the rest."
        htmlFor={`${idPrefix}-floor`}
      >
        <Input id={`${idPrefix}-floor`} type="number" required step="0.01" value={value.minimumPayment}
          onChange={(e) => onChange({ ...value, minimumPayment: num(e.target.value, 0) })} />
      </Field>
      <Field
        label="Principal percent"
        hint="Percent of balance in the issuer's minimum formula, typically 1."
        htmlFor={`${idPrefix}-pct`}
      >
        <Input id={`${idPrefix}-pct`} type="number" required step="0.01" value={value.minPaymentPrincipalPercent}
          onChange={(e) => onChange({ ...value, minPaymentPrincipalPercent: num(e.target.value, 1) })} />
      </Field>
      <Field label="Due day" hint="Day of the month, clamped to month length." htmlFor={`${idPrefix}-dueday`}>
        <Input id={`${idPrefix}-dueday`} type="number" required min="1" max="31" value={value.dueDay}
          onChange={(e) => onChange({ ...value, dueDay: Math.round(num(e.target.value, 1)) })} />
      </Field>
      <Field
        label="Pay off by"
        hint="For a 0% or deferred-interest offer: leave any balance past this date and the whole accrued interest is charged retroactively. A payoff plan clears the card by then ahead of any higher rate. Blank if the card has no deadline."
        htmlFor={`${idPrefix}-deadline`}
      >
        <Input id={`${idPrefix}-deadline`} type="date" value={value.payoffDeadline}
          onChange={(e) => onChange({ ...value, payoffDeadline: e.target.value })} />
      </Field>
    </div>
  );
};

/** Status alongside a plan's name. State reads from fill AND border, so the
 *  four weights stay distinguishable without relying on colour. */
const PLAN_BADGE: Record<'monthly' | 'oneTime' | 'stale' | 'infeasible', { label: string; weight: TagWeight }> = {
  monthly: { label: 'Payment plan', weight: 'ink' },
  oneTime: { label: 'One-time plan', weight: 'bone' },
  stale: { label: 'Out of date', weight: 'redOutline' },
  infeasible: { label: 'Never clears', weight: 'red' },
};

const PlanBadge: React.FC<{ kind: keyof typeof PLAN_BADGE }> = ({ kind }) => (
  <Tag weight={PLAN_BADGE[kind].weight}>{PLAN_BADGE[kind].label}</Tag>
);


interface CreditCardManagerProps {
  creditCards: CreditCard[];
  plans: PayoffPlan[];
  onAddCard: (card: Omit<CreditCard, 'id'>) => void;
  onUpdateCard: (id: string, card: Partial<CreditCard>) => void;
  onDeleteCard: (id: string) => void;
  onCreatePlan: (payload: any) => Promise<any>;
  onUpdatePlan: (id: string, payload: any) => Promise<any>;
  onCancelPlan: (id: string) => Promise<any>;
  // Cash context, so the builder can warn when a plan would push the
  // projected checking balance below the user's threshold.
  currentBalance: number;
  balanceAsOf: Date | null;
  lowBalanceThreshold: number;
  incomes: Income[];
  expenses: Expense[];
  loans: Loan[];
}

export const CreditCardManager: React.FC<CreditCardManagerProps> = ({
  creditCards,
  plans,
  onAddCard,
  onUpdateCard,
  onDeleteCard,
  onCreatePlan,
  onUpdatePlan,
  onCancelPlan,
  currentBalance,
  balanceAsOf,
  lowBalanceThreshold,
  incomes,
  expenses,
  loans
}) => {
  // Builder replaces this list entirely (full-screen), rather than opening a
  // modal — the schedule table and 3-way comparison need the room.
  const [builderCardId, setBuilderCardId] = useState<string | null>(null);
  const [reconcile, setReconcile] = useState<
    { card: CreditCard; plan: PayoffPlan; oldBalance: number; updates: Partial<CreditCard> } | null
  >(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [newCard, setNewCard] = useState({
    name: '',
    balance: 0,
    interestRate: 0,
    minimumPayment: 25,
    minPaymentPrincipalPercent: 1,
    creditLimit: 0,
    dueDay: 1,
    payoffDeadline: ''
  });
  const [editCard, setEditCard] = useState({
    name: '',
    balance: 0,
    interestRate: 0,
    minimumPayment: 25,
    minPaymentPrincipalPercent: 1,
    creditLimit: 0,
    dueDay: 1,
    payoffDeadline: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAddCard({
      ...newCard,
      payoffDeadline: dayFromInput(newCard.payoffDeadline),
      balanceAsOf: new Date()
    });
    setNewCard({
      name: '',
      balance: 0,
      interestRate: 0,
      minimumPayment: 25,
      minPaymentPrincipalPercent: 1,
      creditLimit: 0,
      dueDay: 1,
      payoffDeadline: ''
    });
    setShowAddForm(false);
  };

  // Minimums come from the single canonical formula in finance/minimums.ts.
  // This component used to carry its own copy that capped at the PRE-interest
  // balance, which under-paid the final month.
  const computeMinimum = (balance: number, apr: number, floor: number, principalPct: number): number =>
    minimumPaymentForCard(
      { interestRate: apr, minimumPayment: floor, minPaymentPrincipalPercent: principalPct },
      balance
    );

  const startEdit = (card: CreditCard) => {
    rememberListScroll();
    setEditingCardId(card.id);
    setEditCard({
      name: card.name,
      balance: card.balance,
      interestRate: card.interestRate,
      minimumPayment: card.minimumPayment,
      minPaymentPrincipalPercent: card.minPaymentPrincipalPercent ?? 1,
      creditLimit: card.creditLimit,
      dueDay: card.dueDay || 1,
      payoffDeadline: card.payoffDeadline ? toDayKey(toLocalDay(card.payoffDeadline)) : ''
    });
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCardId) {
      const originalCard = creditCards.find(card => card.id === editingCardId);
      const updates: Partial<CreditCard> = {
        ...editCard,
        payoffDeadline: dayFromInput(editCard.payoffDeadline),
      };

      const balanceChanged = originalCard && originalCard.balance !== editCard.balance;
      if (balanceChanged) {
        updates.balanceAsOf = new Date();
      }

      // A balance edit on a card with an active plan is ambiguous — same
      // payment and finish sooner, or same deadline and pay less? Ask.
      const plan = originalCard ? activeCoveringPlan(originalCard.id, plans) : null;
      if (balanceChanged && originalCard && plan) {
        setReconcile({
          card: originalCard,
          plan,
          oldBalance: originalCard.balance,
          updates,
        });
        setEditingCardId(null);
        return;
      }

      onUpdateCard(editingCardId, updates);
      setEditingCardId(null);
    }
  };

  const cancelEdit = () => {
    setEditingCardId(null);
  };

  /** Every ACTIVE plan on a card — monthly AND one-time. Both are payoff
   *  plans; they differ only in whether they cover statement months. */
  const activePlansForCard = (cardId: string) =>
    plans.filter(pl => pl.creditCardId === cardId && pl.status === 'ACTIVE');

  const hasAnyPlan = (cardId: string) => activePlansForCard(cardId).length > 0;

  const rememberListScroll = useFormScreenScroll(
    showAddForm || editingCardId !== null || builderCardId !== null
  );

  const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

  /* Eight columns at desk width; name, balance and plan at phone width. The
     row is the affordance — clicking it opens the payoff builder for that
     card, which is where plan detail now lives. */
  const COLS = 'grid grid-cols-[1.6fr_1fr_.9fr] md:grid-cols-cards gap-x-4 items-center';

  const ColHead: React.FC<{ label: string; align?: string; deskOnly?: boolean }> = ({
    label, align = 'text-left', deskOnly,
  }) => (
    <div className={cn(TYPE.sectionLabel, 'text-ink-muted', align, deskOnly && 'hidden md:block')}>
      {label}
    </div>
  );

  const renderRow = (card: CreditCard) => {
    const cardPlans = activePlansForCard(card.id);
    const covering = cardPlans.find(pl => pl.mode !== 'LUMP_SUM') ?? null;
    const hasPlan = cardPlans.length > 0;
    const projected = projectCardBalance(card, plans);
    const computedMin = computeMinimum(
      projected, card.interestRate, card.minimumPayment, card.minPaymentPrincipalPercent ?? 1
    );
    const util = card.creditLimit > 0 ? (projected / card.creditLimit) * 100 : 0;
    // Utilisation is a risk reading, so it takes the money ramp: ochre past
    // half the limit, crimson past 80%. Written out rather than interpolated —
    // Tailwind scans source text, so `bg-${tone}` emits no rule at all.
    const utilTone =
      util >= 80
        ? { bar: 'bg-bad', text: 'text-bad' }
        : util >= 50
          ? { bar: 'bg-warn', text: 'text-warn' }
          : { bar: 'bg-ink-soft', text: 'text-ink-soft' };
    const open = () => {
      rememberListScroll();
      setBuilderCardId(card.id);
    };

    return (
      /* The row is not itself a button: it contains one (the overflow menu),
         and a role="button" wrapper would swallow that menu into its own
         accessible name. The card name is the real affordance; the row's
         click handler is a convenience on top of it. */
      <div
        key={card.id}
        onClick={open}
        className={cn(
          COLS,
          'w-full text-left px-5 py-3 border-b border-rule last:border-b-0',
          'cursor-pointer hover:bg-ground-sunk transition-colors duration-[120ms]'
        )}
      >
        <div className="min-w-0">
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              open();
            }}
            className={cn(TYPE.rowTitle, 'text-ink truncate block max-w-full text-left hover:text-accent transition-colors duration-[120ms]')}
          >
            {card.name}
          </button>
          {Math.abs(projected - card.balance) > 0.01 && (
            <div className={cn(TYPE.meta, 'text-ink-muted mt-0.5')}>
              was {usd(card.balance)}
            </div>
          )}
        </div>

        <div className={cn(TYPE.rowFigure, 'text-ink text-right')}>{usd(projected)}</div>

        {/* A 0% promo is the one APR worth reading as good news. */}
        <div
          className={cn(
            'hidden md:block text-right tabular-nums',
            card.interestRate === 0
              ? cn(TYPE.tag, 'text-money-in')
              : card.interestRate >= 27
                ? 'text-[13px] font-semibold text-warn'
                : 'text-[13px] text-ink-soft'
          )}
        >
          {card.interestRate === 0 ? '0% PROMO' : `${card.interestRate.toFixed(2)}%`}
          {/* A 0% card whose deadline is unrecorded looks like the cheapest
              debt in the list. Saying the date turns it into the most urgent. */}
          {card.payoffDeadline && (
            <div
              className={cn(TYPE.metaTight, 'text-warn mt-0.5')}
              title="Any balance left after this date is charged the whole accrued interest retroactively"
            >
              by {toLocalDay(card.payoffDeadline).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              })}
            </div>
          )}
        </div>

        <div className="hidden md:block text-[13px] text-ink-soft text-right tabular-nums">
          {usd(card.creditLimit)}
        </div>

        <div className="hidden md:flex items-center gap-2.5">
          <div className="flex-1 h-[5px] bg-ground-sunk">
            <div
              className={cn('h-[5px]', utilTone.bar)}
              style={{ width: `${Math.min(util, 100)}%` }}
            />
          </div>
          <span className={cn('text-[12px] font-semibold tabular-nums w-9 text-right', utilTone.text)}>
            {util.toFixed(0)}%
          </span>
        </div>

        <div className="hidden md:block text-[13px] text-ink-soft text-center tabular-nums">
          {card.dueDay ?? '—'}
        </div>

        <div className="hidden md:block text-[13px] text-money-out text-right tabular-nums">
          {usd(computedMin)}
        </div>

        <div className="flex items-center justify-end gap-2 min-w-0">
          {hasPlan ? (
            <span className={cn(TYPE.tag, 'text-accent bg-accent-tint px-1.5 py-1')}>
              {covering ? 'PLAN' : 'ONE-TIME'}
            </span>
          ) : (
            <span className={cn(TYPE.meta, 'text-ink-muted')}>—</span>
          )}
          {covering?.isStale && <PlanBadge kind="stale" />}
          {covering && !covering.isFeasible && <PlanBadge kind="infeasible" />}
          {/* Only the overflow menu here: the row itself already opens the
              plan, so a second Edit link would be the same click twice. */}
          <RowActionCell>
            <RowActions
              label={card.name}
              onEdit={() => startEdit(card)}
              onDelete={() => onDeleteCard(card.id)}
            />
          </RowActionCell>
        </div>
      </div>
    );
  };

  const totals = {
    balance: creditCards.reduce((s, c) => s + projectCardBalance(c, plans), 0),
    limit: creditCards.reduce((s, c) => s + c.creditLimit, 0),
    min: creditCards.reduce(
      (s, c) =>
        s +
        computeMinimum(
          projectCardBalance(c, plans),
          c.interestRate,
          c.minimumPayment,
          c.minPaymentPrincipalPercent ?? 1
        ),
      0
    ),
    onPlan: creditCards.filter(c => hasAnyPlan(c.id)).length,
  };
  // Weighted by balance, not a plain mean — a $10k card at 16% and a $700 card
  // at 29% do not average to 22.5% in any sense that matters.
  const weightedApr =
    totals.balance > 0
      ? creditCards.reduce((s, c) => s + projectCardBalance(c, plans) * c.interestRate, 0) /
        totals.balance
      : 0;
  const totalUtil = totals.limit > 0 ? (totals.balance / totals.limit) * 100 : 0;

  const builderCard = builderCardId
    ? creditCards.find(c => c.id === builderCardId) ?? null
    : null;

  if (builderCard) {
    return (
      <PayoffPlanBuilder
        card={builderCard}
        existingPlans={activePlansForCard(builderCard.id)}
        currentBalance={currentBalance}
        balanceAsOf={balanceAsOf}
        lowBalanceThreshold={lowBalanceThreshold}
        incomes={incomes}
        expenses={expenses}
        loans={loans}
        creditCards={creditCards}
        plans={plans}
        onActivate={async (payload, planId) => {
          // The builder tells us whether it was editing a stored plan.
          if (planId) await onUpdatePlan(planId, payload);
          else await onCreatePlan(payload);
        }}
        onRemovePlan={onCancelPlan}
        onClose={() => setBuilderCardId(null)}
      />
    );
  }

  const handleReconcile = async (choice: ReconcileChoice, payload: any | null) => {
    if (!reconcile) return;
    await onUpdateCard(reconcile.card.id, reconcile.updates);
    if (payload) {
      await onUpdatePlan(reconcile.plan.id, payload);
    } else {
      // "Leave as-is": keep the stored amounts but flag the plan, so the UI
      // can say so and the engine recomputes balances against reality.
      await onUpdatePlan(reconcile.plan.id, {
        ...planToPayload(reconcile.plan),
        isStale: true,
      });
    }
    setReconcile(null);
  };

  // Placed after every hook, so hook order never changes between renders.
  const editingCard = editingCardId
    ? creditCards.find(c => c.id === editingCardId) ?? null
    : null;

  const CARD_HINT =
    'Terms drive the computed minimum and every projection. The floor and principal percent come from your statement.';

  if (showAddForm) {
    return (
      <FormScreen
        backLabel="Back to credit cards"
        heading="New card"
        sectionTitle="Card terms"
        hint={CARD_HINT}
        submitLabel="Add card"
        onSubmit={handleSubmit}
        onCancel={() => setShowAddForm(false)}
      >
        <CardFields value={newCard} onChange={setNewCard} idPrefix="new-card" />
      </FormScreen>
    );
  }

  if (editingCard) {
    return (
      <FormScreen
        backLabel="Back to credit cards"
        heading="Edit card"
        subject={editingCard.name}
        sectionTitle="Card terms"
        hint={CARD_HINT}
        submitLabel="Save changes"
        onSubmit={handleEdit}
        onCancel={cancelEdit}
      >
        <CardFields value={editCard} onChange={setEditCard} idPrefix="edit-card" />
      </FormScreen>
    );
  }


  return (
    <>
      {reconcile && (
        <BalanceReconcileDialog
          card={reconcile.card}
          plan={reconcile.plan}
          oldBalance={reconcile.oldBalance}
          newBalance={reconcile.updates.balance ?? reconcile.card.balance}
          onResolve={handleReconcile}
          onCancel={() => setReconcile(null)}
        />
      )}

      <PageHeader
        heading="Credit Cards"
        subhead="Balances, terms and the plans clearing them."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              rememberListScroll();
              setShowAddForm(true);
            }}
          >
            Add card
          </Button>
        }
      />

      <div className="mt-14">
        <PayoffTimeline creditCards={creditCards} plans={plans} />
      </div>

      {creditCards.length === 0 ? (
        <EmptyState>No credit cards added yet</EmptyState>
      ) : (
        /* One table, not two groups: "on a plan" is a column now, so grouping
           by it would say the same thing twice. */
        <div className="mt-11 bg-surface border border-rule-base">
          <div className={cn(COLS, 'px-5 py-2.5 bg-ground-sunk border-b border-rule-base')}>
            <ColHead label="Card" />
            <ColHead label="Balance" align="text-right" />
            <ColHead label="APR" align="text-right" deskOnly />
            <ColHead label="Limit" align="text-right" deskOnly />
            <ColHead label="Utilisation" deskOnly />
            <ColHead label="Due" align="text-center" deskOnly />
            <ColHead label="Minimum" align="text-right" deskOnly />
            <ColHead label="Plan" align="text-right" />
          </div>

          {creditCards.map(renderRow)}

          <div className={cn(COLS, 'px-5 py-3.5 rule-total')}>
            <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>
              {creditCards.length} cards
            </div>
            <div className={cn(TYPE.rowFigure, 'text-ink text-right')}>{usd(totals.balance)}</div>
            <div className={cn(TYPE.meta, 'hidden md:block text-ink-muted text-right')}>
              {weightedApr.toFixed(2)}%
            </div>
            <div className="hidden md:block text-[13px] text-ink-soft text-right tabular-nums">
              {usd(totals.limit)}
            </div>
            <div className={cn(TYPE.meta, 'hidden md:block text-ink-muted')}>
              {totalUtil.toFixed(0)}% utilised
            </div>
            <div className="hidden md:block" />
            <div className="hidden md:block text-[13px] font-semibold text-money-out text-right tabular-nums">
              {usd(totals.min)}
            </div>
            <div className={cn(TYPE.meta, 'text-ink-muted text-right')}>{totals.onPlan} on plan</div>
          </div>
        </div>
      )}
    </>
  );
};
